import React, { useEffect, useState } from 'react';
import { useProfileParams, isAISummariesAccessible } from '../../../../../../utils/common';
import { useGetBusinessPerformanceDetailsQuery } from '../../../../../../redux/services/businessPerformaceAPI';
import styles from './ABOApplicationsTile.module.scss';
import KPITile from '../../../../../common/KPITile/KPITile';
import KPIDetailTile from '../../../../../common/KPIDetailTile/KPIDetailTile';
import Benchmark from '../../../../../common/Benchmark/Benchmark';
import Tooltip from '../../../../../common/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../../common/Icon/Icon';
import { useAppSelector } from '../../../../../../redux/hooks';
import ViewModeSwitcher, {
  ViewMode,
} from '../../../../../common/ViewModeSwitcher/ViewModeSwitcher';
import GraphCard from '../../../../../common/GraphCard/GraphCard';
import InsightAI from '../../../../../common/InsightAI/InsightAI';
import Skeleton from '../../../../../common/Skeleton/Skeleton';
import isEmpty from 'lodash/isEmpty';
import { useGetAGCAISummaryQuery } from '../../../../../../redux/services/agcAIAPI';
import GenericTable from '../../../../../common/GenericTable/GenericTable';
import { isValidLineChartData } from '../../../../../../utils/common';
import { NumberFormatter } from '../../../../../../utils/NumberFormatter';

interface SummaryProp {
  summary?: any;
  loading?: boolean;
  affiliateCode?: string;
}

export const ABOApplicationsTile: React.FC<SummaryProp> = ({
  summary,
  loading = true,
  affiliateCode = '010',
}) => {
  const aboApps = summary?.kpiValue ?? '--';
  const groupSize = summary?.metricSourceData?.[0]?.value ?? '--';
  const benchmark = summary?.benchmark;
  const { t } = useTranslation();
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);

  return (
    <KPITile
      title={t('kpiTitles.aboApplications')}
      dataTestId="abo-applications-tile"
      loading={loading}
    >
      <div className={styles.tile_content}>
        <div className={styles.values_wrapper}>
          <div className={styles.value_wrapper}>
            <div className={styles.value_title}>{t('kpiLabels.aboApplications')}</div>
            <div className={styles.value_content}>{NumberFormatter(affiliateCode, aboApps)}</div>
          </div>
          <div className={`${styles.value_wrapper} ${styles.value_right}`}>
            <div className={styles.value_title}>{t('kpiLabels.monthlyGroupSize')}</div>
            <div className={styles.value_content}>{NumberFormatter(affiliateCode, groupSize)}</div>
          </div>
        </div>

        <div className={styles.bar_wrapper}>
          {
            <Benchmark
              minValue={benchmark?.min}
              maxValue={benchmark?.max}
              averageValue={benchmark?.marketAverage}
              medianValue={benchmark?.median}
              currentBenchmarkValue={summary?.kpiVarianceValue}
              quartileLine={[benchmark?.lowerQuartile ?? 0, benchmark?.upperQuartile ?? 0]}
              benchmarkDataType={summary?.benchmarkDataType}
              isPercentValue={false}
            />
          }
          <div className={styles.bar_footer}>
            {t('market_average')}{' '}
            <span className={styles.bar_footer_value}>{benchmark?.marketAverage}%</span>
            <span>
              <Tooltip
                content={<span>{t('kpiTooltips.aboApplications')}</span>}
                width="250"
                height="auto"
                bottom="30px"
                left="685%"
              >
                <span>
                  <Icon
                    name="info_icon"
                    size={16}
                    color={currentTheme === 'light' ? '#707070' : '#AEAEB2'}
                  />
                </span>
              </Tooltip>
            </span>
          </div>
        </div>
      </div>
    </KPITile>
  );
};

export const ABOApplicationsDetail: React.FC<{
  kpiCode?: string;
  onClose?: () => void;
}> = ({ kpiCode = 'aboApplications', onClose }) => {
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('left');

  const { data: kpiDetailsResponse, isLoading } = useGetBusinessPerformanceDetailsQuery(
    {
      affiliateCode,
      aboNumber,
      payload: { kpis: [{ code: kpiCode }] } as any,
    },
    { skip: !onClose }
  );

  const { data: agcAISummary, isLoading: isLoadingAiSummary } = useGetAGCAISummaryQuery(
    {
      affiliateCode,
      aboNumber,
    },
    {
      skip: Number(affiliateCode) === 0 || aboNumber === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  const detailData = kpiDetailsResponse?.body?.kpis[0] || {};
  const monthlyViewCharts = detailData?.detail?.monthlyView?.chart || [];

  // Extract line chart data
  const lineChartData = monthlyViewCharts.find((chart: any) => chart.chartType === 'lineChart');
  const lineChartDetailData = lineChartData?.chartDetail?.lineChartDetail;
  const hasChartData = isValidLineChartData(lineChartDetailData);

  // Extract table data
  const tableChart = monthlyViewCharts.find((chart: any) => chart.chartType === 'table');
  const tableData = tableChart?.chartDetail?.tableDetail;

  useEffect(() => {
    if (!hasChartData) {
      setViewMode('right');
    }
  }, [hasChartData]);

  if (isLoading) return <Skeleton height="400px" />;
  const aiContent =
    agcAISummary?.body?.summary?.length > 300
      ? `${agcAISummary?.body?.summary?.slice(0, 300)}...`
      : agcAISummary?.body?.summary;

  const headerContent = (
    <div className={styles.header_content}>
      <div className={styles.tile_header}>
        <div className={styles.title}>{t('kpiTitles.aboApplications')}</div>
        <div className={styles.desc}>{t('kpiDescription.aboApplications')}</div>
      </div>
      <div className={styles.chart_view_wrapper}>
        <ViewModeSwitcher
          mode={viewMode}
          onChangeMode={setViewMode}
          disabled={false}
          showSwitch={hasChartData}
        />
      </div>
    </div>
  );

  const leftContent =
    viewMode === 'left' ? (
      isEmpty(lineChartDetailData) ? (
        <div className={styles.no_data}>{t('noDataAvailable')}</div>
      ) : (
        <GraphCard
          lineChartData={lineChartDetailData}
          currency={detailData?.kpiCurrency || 'USD'}
          chartSummaryData={lineChartData?.chartSummary}
        />
      )
    ) : isEmpty(tableData) ? (
      <div className={styles.no_data}>{t('noDataAvailable')}</div>
    ) : (
      <GenericTable
        tableData={tableData}
        kpiCode={kpiCode}
        highlightRows={[0]}
        isOnlyNegativeHighlight={true}
      />
    );

  const rightContent = isAISummariesAccessible() ? (
    isLoadingAiSummary ? (
      <Skeleton height="200px" />
    ) : (
      <InsightAI
        updateDate=""
        content={aiContent}
        title={t('ai_summary')}
        showDropdown={false}
        isDropdownEnabled={true}
        flowerIconSize={24}
        titleSize={14}
      />
    )
  ) : null;
  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="abo-applications-detail"
    />
  );
};

export default ABOApplicationsTile;
